import { z } from 'zod';
import {
  DEFAULT_MOCKUPS_PER_WEEK,
  DEFAULT_R3_TO_MOCKUP_MAX_WEEKS,
  LENGTH_PHASE_KEYS,
  MAX_INTEGRATION_WEEKS,
  PHASE_KEYS,
} from '../planning.js';
import { ALERT_GROUPS, BOARD_COLUMNS } from '../projectBoard.js';
import { PROJECT_NAME_MAX } from '../projectOrder.js';
import { IsoDate, IsoDateTime, Uuid } from './common.js';
import { MAX_STEP_NOTIFY } from './settings.js';

/** Projets, planning et agenda (lot D1a, routes du CDC §3.7). */

export const PhaseKeySchema = z.enum(PHASE_KEYS);

/** `projects.phases` : date de début de chaque phase, ou null / absente. */
export const ProjectPhases = z.object(
  Object.fromEntries(PHASE_KEYS.map((key) => [key, IsoDate.nullable().optional()])) as Record<
    (typeof PHASE_KEYS)[number],
    z.ZodOptional<z.ZodNullable<typeof IsoDate>>
  >,
);
export type ProjectPhases = z.infer<typeof ProjectPhases>;

/** Rendez-vous calés avec le client (lot K). */
export const MeetingKeySchema = z.enum(['R3', 'R4', 'R5']);

/**
 * Lot K « Périodes de rendez-vous » (`projects.meetings`) : à la préparation, chaque R3, R4 et R5 reçoit une
 * période pour le caler avec le client (`windowStart` = la date prévue par le planning, `windowEnd` =
 * `windowStart + planning.meetingWindowDays`) ; une fois fixé, `at` (date et heure) et `eventId` (événement
 * Google de l'agenda de la personne des rendez-vous) sont renseignés. Tant qu'il n'est pas fixé, la date de la
 * phase (`phases.R3`…) suit `windowStart`. Lot BQ : tout projet ouvert a une entrée pour chaque rendez-vous
 * applicable (R3, R4, R5 ; R5 seul pour un partenaire), quel que soit le chemin qui l'a créé (`completeMeetings`).
 */
export const ProjectMeeting = z.object({
  windowStart: IsoDate,
  windowEnd: IsoDate,
  at: IsoDateTime.nullable(),
  eventId: z.string().nullable().optional(),
  /**
   * Lot BQ : rendez-vous déjà dépassé par une étape cochée (projet importé déjà avancé) : il n'est plus à caler, ni
   * plage au planning ni carte « Fixer le … ». Sa date de phase, s'il en a une, reste affichée comme un rendez-vous
   * passé. Le fixer (proposition de l'agenda, événement reconnu) retire cette marque.
   */
  passed: z.boolean().optional(),
  /**
   * Lot BQ : d'où vient la date : `agenda` = repris d'un événement existant de l'agenda (rapprochement sûr, proposition
   * confirmée, ou événement déplacé dans l'agenda que le Cockpit a suivi). La fiche l'affiche (« repris de l'agenda »).
   */
  source: z.literal('agenda').optional(),
  /**
   * Lot BQ : position connue de l'événement quand le Cockpit a déplacé le rendez-vous sans déplacer l'événement (étape
   * « Déplacer dans l'agenda » décochée). Le rapprochement ne suit l'agenda que si l'événement a bougé depuis : il ne
   * défait jamais un déplacement validé.
   */
  eventAt: IsoDateTime.optional(),
});
export type ProjectMeeting = z.infer<typeof ProjectMeeting>;

export const ProjectMeetings = z.object({
  R3: ProjectMeeting.optional(),
  R4: ProjectMeeting.optional(),
  R5: ProjectMeeting.optional(),
});
export type ProjectMeetings = z.infer<typeof ProjectMeetings>;

const DurationDays = z.int().min(0).max(365);

/**
 * Lot O « Durées de chaque projet » (`projects.durations`) : durées propres à ce projet, qui remplacent celles
 * des Réglages (`planning.durations`) dans ses recalculs (ex. « Fixer le R3 »). Vide : les Réglages s'appliquent.
 */
export const ProjectDurations = z.object({
  r3ToMockup: DurationDays.optional(),
  mockupToR4: DurationDays.optional(),
  r4ToFeedback: DurationDays.optional(),
  feedbackToIntegration: DurationDays.optional(),
});
export type ProjectDurations = z.infer<typeof ProjectDurations>;

/**
 * Références du plan `project.resize` (lot O) : nouvelles longueurs de phases (jours, de leur début au début de
 * la phase suivante du projet) et/ou nouvelle durée d'intégration (semaines). Envoyées par le Gantt (bord droit
 * d'une barre) ou par « Durées de ce projet » du panneau ; le serveur construit le plan à partir de l'état réel.
 */
export const ResizeProjectRefs = z
  .object({
    projectId: Uuid,
    lengths: z
      .object(
        Object.fromEntries(LENGTH_PHASE_KEYS.map((key) => [key, DurationDays.optional()])) as Record<
          (typeof LENGTH_PHASE_KEYS)[number],
          z.ZodOptional<typeof DurationDays>
        >,
      )
      .default({}),
    integrationWeeks: z.int().min(1).max(MAX_INTEGRATION_WEEKS).optional(),
  })
  .refine((r) => r.integrationWeeks !== undefined || Object.values(r.lengths).some((v) => v !== undefined), {
    message: 'Une durée au moins est attendue',
  });
export type ResizeProjectRefs = z.input<typeof ResizeProjectRefs>;

/**
 * Lot AL : type de projet (`projects.project_type`). `evolusite` (défaut) : R3, maquette, R4, retours, intégration,
 * R5 ; `partner` : Evolusite n'est qu'intégrateur (devis de frais d'intégration + abonnement), seulement
 * l'intégration puis le R5, l'amont étant géré par le partenaire.
 */
export const PROJECT_TYPES = ['evolusite', 'partner'] as const;
export const ProjectType = z.enum(PROJECT_TYPES);
export type ProjectType = z.infer<typeof ProjectType>;
export const PROJECT_TYPE_LABELS: Record<ProjectType, string> = {
  evolusite: 'Evolusite',
  partner: 'Partenaire',
};

export const ProjectStatus = z.enum(['PLANNED', 'ACTIVE', 'DONE', 'PAUSED']);
export type ProjectStatus = z.infer<typeof ProjectStatus>;

export const ProjectStep = z.object({
  id: Uuid,
  position: z.int(),
  label: z.string(),
  phase: PhaseKeySchema.nullable(),
  whoUserId: Uuid.nullable(),
  /** Lot M : membres à prévenir une fois l'étape cochée (0 à n, dans l'ordre du modèle). */
  notifyUserIds: z.array(Uuid),
  message: z.string().nullable(),
  taskLabel: z.string().nullable(),
  done: z.boolean(),
  addedManually: z.boolean(),
});
export type ProjectStep = z.infer<typeof ProjectStep>;

/** Phase qui tombe pendant un congé de la personne qui la fait (contour rouge du Gantt). */
export const ProjectConflict = z.object({
  phase: PhaseKeySchema,
  from: IsoDate,
  to: IsoDate,
  userId: Uuid,
  absenceLabel: z.string(),
  absenceFrom: IsoDate,
  absenceTo: IsoDate,
});
export type ProjectConflict = z.infer<typeof ProjectConflict>;

export const ProjectSummary = z.object({
  id: Uuid,
  name: z.string(),
  clientId: Uuid.nullable(),
  clientName: z.string(),
  quoteId: Uuid.nullable(),
  integrationWeeks: z.int(),
  subcontracted: z.boolean(),
  /** Lot AL : projet Evolusite ou partenaire (intégration seule). */
  projectType: ProjectType.default('evolusite'),
  /**
   * Lot AL : nom du partenaire d'un projet partenaire (apporteur du Cockpit rattaché, sinon le texte de la fiche
   * Axonaut noté à la préparation) ; null : inconnu, ou projet Evolusite.
   */
  partnerName: z.string().nullable().default(null),
  status: ProjectStatus,
  phases: ProjectPhases,
  /** Périodes et rendez-vous fixés (lot K) ; lot BQ : une entrée par rendez-vous applicable sur un projet ouvert. */
  meetings: ProjectMeetings.default({}),
  /** Durées propres au projet (lot O) ; vide : celles des Réglages. */
  durations: ProjectDurations.default({}),
  /** Lien vers le site du client dans l'admin Evolusite (modèle de la connexion du back), si connu. */
  adminSiteUrl: z.string().nullable().optional(),
  /** Origine : `app` ou `sheet-timeline` (import). */
  source: z.string(),
  /** Avancement des étapes (« 5/12 »). */
  progress: z.object({ done: z.int(), total: z.int() }),
  /** Prochaine étape non cochée. */
  nextStep: z.object({ id: Uuid, label: z.string(), whoUserId: Uuid.nullable() }).nullable(),
  conflicts: z.array(ProjectConflict),
  /** Lot BL : date « en ligne au plus tard » du client (reprise du devis, modifiable dans le volet), null : aucune. */
  onlineBy: IsoDate.nullable().optional(),
});
export type ProjectSummary = z.infer<typeof ProjectSummary>;

/** `GET /api/projects` : tableau brut (non paginé), dans l'ordre du planning (rangé à la main, sinon par date ; lot AR). */
export const ProjectList = z.array(ProjectSummary);
export type ProjectList = z.infer<typeof ProjectList>;

/** `GET /api/projects/:id` */
export const ProjectDetail = ProjectSummary.extend({ steps: z.array(ProjectStep) });
export type ProjectDetail = z.infer<typeof ProjectDetail>;

export const ProjectIdParams = z.object({ id: Uuid });

/* ------------------------------------------------------------------ lot BR : Suivi, Rendez-vous, « À regarder » */

export const BoardColumnSchema = z.enum(BOARD_COLUMNS);

export const BoardMeetingDto = z.object({
  key: MeetingKeySchema,
  state: z.enum(['done', 'set', 'past', 'tofix', 'late']),
  at: IsoDateTime.nullable(),
  date: IsoDate.nullable(),
  windowStart: IsoDate.nullable(),
  windowEnd: IsoDate.nullable(),
  canFix: z.boolean(),
  soon: z.boolean(),
  stepId: Uuid.nullable(),
  fromAgenda: z.boolean(),
});
export type BoardMeetingDto = z.infer<typeof BoardMeetingDto>;

export const ProjectAlertDto = z.object({
  group: z.enum(ALERT_GROUPS),
  level: z.enum(['bad', 'warn']),
  meeting: MeetingKeySchema.nullable(),
  text: z.string(),
  short: z.string(),
  summary: z.string(),
});
export type ProjectAlertDto = z.infer<typeof ProjectAlertDto>;

/** Un projet vu par le Suivi, la vue Rendez-vous, « À regarder » et la fiche (`GET /api/projects/board`). */
export const ProjectBoardItem = z.object({
  id: Uuid,
  name: z.string(),
  clientId: Uuid.nullable(),
  clientName: z.string(),
  projectType: ProjectType,
  partnerName: z.string().nullable(),
  status: ProjectStatus,
  /** Mise en ligne prévue (phase R5). */
  onlineOn: IsoDate.nullable(),
  /** De quoi refaire le calcul à l'écran (déplacements en attente, étape cochée pendant les 5 s d'« Annuler »). */
  phases: ProjectPhases,
  plannedMeetings: ProjectMeetings,
  steps: z.array(
    z.object({
      id: Uuid,
      label: z.string(),
      phase: PhaseKeySchema.nullable(),
      done: z.boolean(),
      whoUserId: Uuid.nullable(),
    }),
  ),
  lastExchangeOn: IsoDate.nullable(),
  lastStepDoneOn: IsoDate.nullable(),
  /** Colonne affichée. */
  column: BoardColumnSchema,
  /** Colonne que donneraient les étapes cochées. */
  autoColumn: BoardColumnSchema,
  /** Placement à la main en cours (qui, quand) ; null : la colonne suit les étapes. */
  manual: z
    .object({ column: BoardColumnSchema, at: IsoDateTime.nullable(), byName: z.string().nullable() })
    .nullable(),
  progress: z.object({ done: z.int(), total: z.int() }),
  nextStep: z.object({ id: Uuid, label: z.string(), whoUserId: Uuid.nullable() }).nullable(),
  meetings: z.array(BoardMeetingDto),
  alerts: z.array(ProjectAlertDto),
  /** Fin du projet : date notée quand il passe à « Terminé », sinon sa mise en ligne (projets anciens). */
  finishedOn: IsoDate.nullable(),
});
export type ProjectBoardItem = z.infer<typeof ProjectBoardItem>;

export const ProjectBoard = z.object({ today: IsoDate, items: z.array(ProjectBoardItem) });
export type ProjectBoard = z.infer<typeof ProjectBoard>;

/** `PUT /api/projects/:id/column` : placement à la main, écriture interne sans plan, tracée à l'audit. */
export const SetBoardColumnBody = z.object({ column: BoardColumnSchema });
export type SetBoardColumnBody = z.infer<typeof SetBoardColumnBody>;

/** `PUT /api/projects/:id/client` : relier le projet à un client Axonaut déjà connu (choix d'un membre, jamais automatique). */
export const LinkProjectClientBody = z.object({ clientId: Uuid });
export type LinkProjectClientBody = z.infer<typeof LinkProjectClientBody>;

/** Réponse d'une écriture du Suivi : la carte à jour et de quoi l'annuler (null : rien n'a changé). */
export const ProjectBoardChange = z.object({ item: ProjectBoardItem, undoId: Uuid.nullable() });
export type ProjectBoardChange = z.infer<typeof ProjectBoardChange>;

/** `POST /api/projects/:id/undo` : « Annuler » d'une écriture du Suivi (5 secondes, par son auteur). */
export const UndoProjectChangeBody = z.object({ undoId: Uuid });
export type UndoProjectChangeBody = z.infer<typeof UndoProjectChangeBody>;

/** Événement SSE `project.changed` : le Suivi d'un projet a changé (colonne, client relié). */
export const ProjectChangedEvent = z.object({ projectId: Uuid });
export type ProjectChangedEvent = z.infer<typeof ProjectChangedEvent>;

/**
 * Lot AR : `PUT /api/projects/order` : les identifiants de la liste affichée (filtrée ou non), dans le nouvel
 * ordre. Les projets masqués gardent leur place dans l'ordre complet (`mergeProjectOrder`). Écriture interne,
 * sans plan (décision de Maxime du 15/10/2026).
 */
export const ProjectOrderBody = z.object({ ids: z.array(Uuid).min(1).max(2000) });
export type ProjectOrderBody = z.infer<typeof ProjectOrderBody>;

/** Lot AR : `GET|PUT|DELETE /api/projects/order` : ordre rangé à la main ou par date, et qui l'a rangé en dernier. */
export const ProjectOrderState = z.object({
  /** true : au moins un projet a une position (ordre rangé à la main) ; false : ordre par date. */
  manual: z.boolean(),
  /** Dernier rangement à la main (ou dernier « Remettre l'ordre par date ») : quand, et par qui. */
  updatedAt: IsoDateTime.nullable(),
  updatedByName: z.string().nullable(),
});
export type ProjectOrderState = z.infer<typeof ProjectOrderState>;

/** Lot AR : `PATCH /api/projects/:id` : renommer le projet dans le Cockpit (`name` seulement). */
export const RenameProjectBody = z.object({ name: z.string().trim().min(1).max(PROJECT_NAME_MAX) });
export type RenameProjectBody = z.infer<typeof RenameProjectBody>;

/**
 * Lot BL : `PATCH /api/projects/:id` accepte aussi `onlineBy` (date « en ligne au plus tard » du client, null :
 * aucune), écriture interne auditée comme le renommage. Au moins un des deux champs.
 */
export const UpdateProjectBody = z
  .object({
    name: z.string().trim().min(1).max(PROJECT_NAME_MAX).optional(),
    onlineBy: IsoDate.nullable().optional(),
  })
  .refine((b) => b.name !== undefined || b.onlineBy !== undefined, {
    message: 'Aucune modification demandée',
  });
export type UpdateProjectBody = z.infer<typeof UpdateProjectBody>;

/**
 * Lot BQ : événement de l'agenda qui RESSEMBLE à un rendez-vous d'un projet (son titre donne le type R3/R4/R5 et le
 * nom du projet ou du client), sans lien sûr : il ne fixe rien tout seul (décision de Maxime du 02/11/2026), il est
 * proposé, à confirmer en un clic. Lecture : `GET /api/projects/meeting-proposals` (`?projectId=` pour la fiche).
 * Confirmer : plan `meeting.confirm` (`runDirect`, « Annuler » pendant 5 s) ; écarter : `POST …/dismiss`.
 * - `eventId` : l'identifiant Google, à renvoyer tel quel à la confirmation ou au refus ;
 * - `date` et `time` : date et heure à Paris (`time` au format « 14:30 ») ;
 * - `current` : où en est le rendez-vous dans le Cockpit (`windowStart`, `windowEnd`, `passed` : déjà dépassé par une
 *   étape cochée, la proposition sert alors à noter la vraie date).
 */
export const MeetingProposal = z.object({
  projectId: Uuid,
  projectName: z.string(),
  clientName: z.string(),
  meeting: MeetingKeySchema,
  eventId: z.string(),
  title: z.string(),
  startAt: IsoDateTime,
  endAt: IsoDateTime,
  date: IsoDate,
  time: z.string(),
  reason: z.literal('title'),
  current: z.object({ windowStart: IsoDate, windowEnd: IsoDate, passed: z.boolean() }),
});
export type MeetingProposal = z.infer<typeof MeetingProposal>;

export const MeetingProposalQuery = z.object({ projectId: Uuid.optional() });
export const MeetingProposalList = z.array(MeetingProposal);
export type MeetingProposalList = z.infer<typeof MeetingProposalList>;

export const MeetingParams = z.object({ id: Uuid, meeting: MeetingKeySchema });

/** Lot BQ : `POST|DELETE /api/projects/:id/meetings/:meeting/dismiss` : écarter ou reproposer cet événement. */
export const MeetingProposalBody = z.object({ eventId: z.string().min(1).max(1024) });
export type MeetingProposalBody = z.infer<typeof MeetingProposalBody>;

/** Membre affiché dans le planning (noms et couleurs des congés, choix « fait par »). */
export const PlanningMember = z.object({
  id: Uuid,
  name: z.string(),
  initials: z.string(),
  color: z.string(),
});
export type PlanningMember = z.infer<typeof PlanningMember>;

export const AbsenceDto = z.object({
  id: Uuid,
  userId: Uuid,
  from: IsoDate,
  to: IsoDate,
  label: z.string(),
});
export type AbsenceDto = z.infer<typeof AbsenceDto>;

export const ProjectLoadQuery = z.object({ from: IsoDate.optional(), to: IsoDate.optional() });

/**
 * `GET /api/projects/load` : charge par semaine (D0 `weekLoad`) sur une période, et le contexte du
 * planning nécessaire au Gantt (congés, membres, rôles, capacité, semaines de fermeture).
 */
export const ProjectLoad = z.object({
  from: IsoDate,
  to: IsoDate,
  today: IsoDate,
  capacity: z.object({ usual: z.int(), max: z.int() }),
  closedWeeks: z.array(IsoDate),
  weeks: z.array(z.object({ monday: IsoDate, count: z.int(), closed: z.boolean() })),
  absences: z.array(AbsenceDto),
  members: z.array(PlanningMember),
  roles: z.object({ mockup: Uuid, meetings: Uuid, integration: Uuid }).nullable(),
  /** Lot K : intégration proposée par défaut (Réglages > Planning), lue aussi par les membres. */
  defaultIntegrationWeeks: z.int().min(1).max(3).default(2),
  /** Lot K : jours de la période pour fixer un rendez-vous. */
  meetingWindowDays: z.int().min(1).default(14),
  /** Lot AE : maquettes acceptées par semaine (Réglages > Planning) et écart maximum R3 → maquette (semaines). */
  mockupsPerWeek: z.int().min(1).default(DEFAULT_MOCKUPS_PER_WEEK),
  r3ToMockupMaxWeeks: z.int().min(1).default(DEFAULT_R3_TO_MOCKUP_MAX_WEEKS),
  /** Lot AE : maquettes qui démarrent chaque semaine de la période (hors sous-traitance), mêmes lundis que `weeks`. */
  mockupWeeks: z.array(z.object({ monday: IsoDate, count: z.int() })).default([]),
});
export type ProjectLoad = z.infer<typeof ProjectLoad>;

/** `POST /api/projects/simulate` (simulateur « Un devis vient de signer »). */
export const SimulateProjectBody = z.object({
  /** Démarrage au plus tôt à cette date (« dans 1 mois », « à une date précise »). */
  startNotBefore: IsoDate.optional(),
  /** Absente : la durée par défaut des Réglages (`planning.defaultIntegrationWeeks`, lot K). */
  integrationWeeks: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
  subcontracted: z.boolean().default(false),
  /**
   * Lot AL : devis simulé (sa date de signature et son client) ; avec un client partenaire, le type `partner` est
   * proposé d'office.
   */
  quoteId: Uuid.optional(),
  /** Lot AL : type choisi à la main ; absent : celui détecté d'après le client du devis (sinon `evolusite`). */
  projectType: ProjectType.optional(),
});
export type SimulateProjectBody = z.input<typeof SimulateProjectBody>;

export const SimulatedDates = z.object({
  r3: IsoDate,
  mockup: IsoDate,
  r4: IsoDate,
  feedback: IsoDate,
  integration: IsoDate,
  r5: IsoDate,
  notes: z.array(z.string()),
  delayedWeeks: z.number(),
  /** Lot AE : semaines de décalage de la maquette (semaines pleines, fermées, congés de la maquettiste). */
  mockupDelayedWeeks: z.number().default(0),
});
export type SimulatedDates = z.infer<typeof SimulatedDates>;

/**
 * Lot AL : planning d'un projet partenaire (intégration au plus tôt = signature + `partnerIntegrationDelayDays`,
 * puis R5 à la fin de l'intégration). Aucune maquette.
 */
export const PartnerSimulation = z.object({
  signedOn: IsoDate.nullable(),
  delayDays: z.int().min(0),
  earliest: IsoDate,
  integration: IsoDate,
  r5: IsoDate,
  notes: z.array(z.string()),
  /** Notes jointes par « · ». */
  note: z.string(),
  delayedWeeks: z.number(),
  /** Nom du partenaire (apporteur de la fiche Axonaut rapproché du Cockpit, sinon le texte brut, sinon null). */
  partnerName: z.string().nullable(),
  /** Même calcul en acceptant `capacity.max` intégrations par semaine. */
  atMaxCapacity: z.object({ integration: IsoDate, r5: IsoDate }),
});
export type PartnerSimulation = z.infer<typeof PartnerSimulation>;

export const SimulateProjectResult = SimulatedDates.extend({
  /** Notes jointes par « · », ou « aucun conflit avec les intégrations ni les congés ». */
  note: z.string(),
  capacity: z.object({ usual: z.int(), max: z.int() }),
  /** Même simulation en acceptant `capacity.max` intégrations par semaine. */
  atMaxCapacity: SimulatedDates,
  /** Lot AL : type retenu (choisi, sinon détecté) et type détecté d'après le client du devis. */
  projectType: ProjectType.default('evolusite'),
  detectedType: ProjectType.default('evolusite'),
  /** Lot AL : planning partenaire quand le type retenu est `partner` (null sinon). */
  partner: PartnerSimulation.nullable().default(null),
});
export type SimulateProjectResult = z.infer<typeof SimulateProjectResult>;

// ------------------------------------------------------------------ Simulation de plusieurs devis (lot BL)

/**
 * Lot BL : un devis de `POST /api/projects/simulate-many`, avec ses réglages (absents : ceux du simulateur, d'après
 * le devis et les Réglages). `start` = démarrage au plus tôt ; `onlineBy` absent : la date enregistrée sur le devis,
 * null : aucune date limite pour cette simulation.
 */
export const SimulateManyItem = z.object({
  quoteId: Uuid,
  start: IsoDate.optional(),
  integrationWeeks: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
  subcontracted: z.boolean().optional(),
  projectType: ProjectType.optional(),
  onlineBy: IsoDate.nullable().optional(),
});
export type SimulateManyItem = z.infer<typeof SimulateManyItem>;

/** Lot BL : les devis dans l'ordre où le planning les place ; « Accepter des grosses semaines ». */
export const SimulateManyBody = z.object({
  items: z.array(SimulateManyItem).min(1).max(30),
  allowBigWeeks: z.boolean().default(false),
});
export type SimulateManyBody = z.input<typeof SimulateManyBody>;

/** `fits` tient, `big_week` tient avec une grosse semaine, `late` dépasse sa date limite, `none` pas de date. */
export const SimulationStatusSchema = z.enum(['fits', 'big_week', 'late', 'none']);

export const SimulateManyPlacementDto = z.object({
  quoteId: Uuid,
  phases: ProjectPhases,
  r5: IsoDate,
  status: SimulationStatusSchema,
  notes: z.array(z.string()),
  /** Ajouts du lot BL (hors contrat minimal) : réglages retenus et détail de la grosse semaine. */
  integration: IsoDate,
  integrationWeeks: z.int(),
  subcontracted: z.boolean(),
  projectType: ProjectType,
  start: IsoDate.nullable(),
  onlineBy: IsoDate.nullable(),
  bigWeeks: z.array(IsoDate),
  withoutBigWeekR5: IsoDate.nullable(),
});
export type SimulateManyPlacementDto = z.infer<typeof SimulateManyPlacementDto>;

export const SimulateManyResult = z.object({
  placements: z.array(SimulateManyPlacementDto),
  weekLoad: z.array(z.object({ monday: IsoDate, count: z.int(), max: z.int() })),
  existing: z.array(
    z.object({
      projectId: Uuid,
      onlineBy: IsoDate,
      r5: IsoDate.nullable(),
      status: SimulationStatusSchema,
    }),
  ),
  /** Capacité des Réglages (habituelle, maximale), pour la ligne de charge. */
  capacity: z.object({ usual: z.int(), max: z.int() }),
});
export type SimulateManyResult = z.infer<typeof SimulateManyResult>;

/**
 * Événements des agendas personnels des membres (`calendar_events`, lot H). R1 et R2 sont posés par les membres
 * eux-mêmes (lecture seule) ; R3, R4 et R5 par le cockpit, dans l'agenda de la personne du rôle « rendez-vous ».
 */
export const CalendarEventKind = z.enum(['R1', 'R2', 'R3', 'R4', 'R5', 'ONLINE', 'ABSENCE', 'OTHER']);
export type CalendarEventKind = z.infer<typeof CalendarEventKind>;

export const CalendarEventDto = z.object({
  id: Uuid,
  title: z.string(),
  startAt: IsoDateTime,
  endAt: IsoDateTime,
  allDay: z.boolean(),
  kind: CalendarEventKind,
  projectId: Uuid.nullable(),
  /** Congé : la personne absente. */
  userId: Uuid.nullable(),
  /**
   * Membres dont l'agenda personnel contient cet événement (lot H) : un événement partagé (même iCalUID, ex.
   * Inès et Maxime invités) n'apparaît qu'une fois, avec ses deux membres. Vide pour une ligne héritée de
   * l'ancien agenda partagé (retirée à la synchro suivante).
   */
  ownerUserIds: z.array(Uuid),
  /** Lot AO : clé du R1 (`r1KeyOf`) pour ouvrir son volet (`/agenda?r1=<clé>`) ; absente sur les autres. */
  r1Key: z.string().min(1).max(300).nullable().optional(),
});
export type CalendarEventDto = z.infer<typeof CalendarEventDto>;

export const AgendaWeekQuery = z.object({ start: IsoDate.optional() });

/** `GET /api/agenda/week?start=` : la semaine du lundi `start` (par défaut la semaine en cours). */
export const AgendaWeek = z.object({
  start: IsoDate,
  end: IsoDate,
  today: IsoDate,
  events: z.array(CalendarEventDto),
});
export type AgendaWeek = z.infer<typeof AgendaWeek>;

/**
 * Lot AF [23] : `GET /api/agenda/upcoming` : les rendez-vous R1 à R5 à venir de tous les agendas (début après
 * maintenant), une ligne par rendez-vous même s'il est dans plusieurs agendas, et leur nombre par type.
 */
export const AgendaUpcoming = z.object({
  today: IsoDate,
  counts: z.object({
    R1: z.int().min(0),
    R2: z.int().min(0),
    R3: z.int().min(0),
    R4: z.int().min(0),
    R5: z.int().min(0),
  }),
  events: z.array(CalendarEventDto),
});
export type AgendaUpcoming = z.infer<typeof AgendaUpcoming>;

/**
 * `PATCH /api/projects/:id/steps/:stepId` : changer qui fait une étape sur ce projet (maquette : choix
 * « Fait par » du panneau) et, depuis le lot M, qui prévenir une fois cochée (liste, 0 à n membres). Champ
 * absent : inchangé ; au moins un des deux. Modification interne, sans plan (comme `PATCH /api/actions/:id`).
 */
export const ProjectStepParams = z.object({ id: Uuid, stepId: Uuid });
export const UpdateProjectStepBody = z
  .object({
    whoUserId: Uuid.nullable().optional(),
    notifyUserIds: z.array(Uuid).max(MAX_STEP_NOTIFY).optional(),
  })
  .refine((body) => body.whoUserId !== undefined || body.notifyUserIds !== undefined, {
    message: 'whoUserId ou notifyUserIds attendu',
  });
export type UpdateProjectStepBody = z.infer<typeof UpdateProjectStepBody>;
